import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ComponentPropsWithoutRef,
	type ComponentType,
	type ReactNode,
	useLayoutEffect,
	memo,
	useCallback,
	useState,
} from 'react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import {
	graphql,
	type PreloadedQuery,
	type RefetchFn,
	usePreloadedQuery,
	useRefetchableFragment,
	useFragment,
	usePaginationFragment,
} from 'react-relay';
import CurrencyFieldChangeboarding from '@jira/cmp-currency-field-changeboarding/src/ui/components/CmpCurrencyFormattingFlag.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { FilterRefetchQuery } from '@atlassian/jira-relay/src/__generated__/FilterRefetchQuery.graphql';
import type { IssueNavigatorDetailedViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorDetailedViewPaginationQuery.graphql';
import type { IssueNavigatorJiraDetailedViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorJiraDetailedViewPaginationQuery.graphql';
import type { IssueNavigatorGroupedListViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorGroupedListViewPaginationQuery.graphql';
import type { IssueNavigatorJiraGroupedListViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorJiraGroupedListViewPaginationQuery.graphql';
import type { IssueNavigatorIssueSearchRefetchQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorIssueSearchRefetchQuery.graphql';
import type { IssueNavigatorListViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorListViewPaginationQuery.graphql';

import type { IssueNavigatorJiraListViewPaginationQuery } from '@atlassian/jira-relay/src/__generated__/IssueNavigatorJiraListViewPaginationQuery.graphql';
import type { main_FilterQuery as FilterQueryType } from '@atlassian/jira-relay/src/__generated__/main_FilterQuery.graphql';
import type { main_issueNavigator_IssueNavigator_jiraDetailedViewData$key as JiraDetailedViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_jiraDetailedViewData.graphql';
import type { main_issueNavigator_IssueNavigator_jiraGroupedListViewData$key as JiraGroupedListViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_jiraGroupedListViewData.graphql';
import type { main_issueNavigator_IssueNavigator_jiraListViewData$key as JiraListViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_jiraListViewData.graphql';
import type { main_issueNavigator_IssueNavigator_detailedViewData$key as DetailedViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_detailedViewData.graphql';
import type { main_issueNavigator_IssueNavigator_filterQuery$key as FilterFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_filterQuery.graphql';
import type { main_issueNavigator_IssueNavigator_groupedListViewData$key as GroupedListViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_groupedListViewData.graphql';
import type { main_issueNavigator_IssueNavigator_issueQuery$key as IssueSearchConditionFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_issueQuery.graphql';
import type { main_issueNavigator_IssueNavigator_jira$key as JqlBuilderWithAiKey } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_jira.graphql';
import type { main_issueNavigator_IssueNavigator_listViewData$key as ListViewDataFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_listViewData.graphql';
import type { main_issueNavigator_IssueNavigator_refetchQuery$key as IssueNavigatorRefetchFragment } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_refetchQuery.graphql';
import type { main_issueNavigator_IssueNavigator_userPreferences$key as UserPreferencesFragmentKey } from '@atlassian/jira-relay/src/__generated__/main_issueNavigator_IssueNavigator_userPreferences.graphql';
import type { main_IssueNavigatorQuery as IssueNavigatorQueryType } from '@atlassian/jira-relay/src/__generated__/main_IssueNavigatorQuery.graphql';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { SidebarContainer } from '@atlassian/jira-platform-sidebar/src/controllers/use-sidebar/index.tsx';
import { ConditionalFormatting as ConditionalFormattingOld } from '@atlassian/jira-issue-navigator-action-conditional-formatting/src/ui/container/index.tsx';
import { ConditionalFormattingSidebarContainer } from '@atlassian/jira-issue-navigator-conditional-formatting-sidebar/src/ui/format-rules-sidebar/sidebar/ConditionalFormattingSidebarContainer.tsx';
import type { ConditionalFormattingSidebarContainer_view$key } from '@atlassian/jira-relay/src/__generated__/ConditionalFormattingSidebarContainer_view.graphql.ts';
import type { container_issueNavigatorActionConditionalFormatting_ConditionalFormattingInner$key } from '@atlassian/jira-relay/src/__generated__/container_issueNavigatorActionConditionalFormatting_ConditionalFormattingInner.graphql.ts';
import type { ConditionalFormattingSidebarContainer_project$key } from '@atlassian/jira-relay/src/__generated__/ConditionalFormattingSidebarContainer_project.graphql.ts';
import type { JQLBuilderWithAIFilterRefinementQuery } from '@atlassian/jira-relay/src/__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql.ts';
import {
	FilterTypes,
	SearchInputTypes,
	NIN_SIDEBAR_SCOPE,
	ISSUE_NAVIGATOR_SIDEBAR_ID,
} from './common/constants.tsx';
import type {
	CustomHeaderProps,
	FilterId,
	FilterJql,
	SearchInputType,
	IssueNavigatorViewId,
	OverridableIssueTableProps,
} from './common/types.tsx';
import { markOnce, marks } from './common/utils/performance-analytics.tsx';
import { AppContextualAnalytics } from './controllers/app-contextual-analytics/index.tsx';
import { ActiveJqlProvider } from './services/active-jql/index.tsx';
import { FilterQueryProvider } from './services/filter-query/index.tsx';
import { IssueSearch } from './ui/issue-search/index.tsx';
import type { OverridableJqlBuilderProps } from './ui/jql-builder/index.tsx';
import IssueNavigatorUI from './ui/main.tsx';
import { SelectedIssue } from './ui/selected-issue/index.tsx';
import { SelectedViewContainer } from './ui/selected-view/index.tsx';
import { InfiniteScrollProvider } from './services/infinite-scroll/index.tsx';
import {
	FlatListIssuesContainer,
	useFlatListIssuesActions,
} from './controllers/flat-list-issues/index.tsx';
import { useIssueNavigatorFeatures } from './controllers/features-context/index.tsx';
import { IssueSearchInputProvider } from './ui/issue-search-input-provider/IssueSearchInputProvider.tsx';

export type Props = {
	issueKey: IssueKey;
	CustomHeader?: ComponentType<CustomHeaderProps>;
	ActionMenu?: ComponentType<CustomHeaderProps>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HeaderSkeletonImage: ComponentType<Record<any, any>>;
	defaultJql: string;
	/** @deprecated Remove on cleanup of jira_nin_last_used_jql_tech_changes  */
	searchInput: SearchInputType;
	onFilterKeyToJql?: (filter: string) => string | undefined;
	queryReference: PreloadedQuery<IssueNavigatorQueryType>;
	filterQueryReference: PreloadedQuery<FilterQueryType>;
	onChangeIssue: JSX.LibraryManagedAttributes<
		typeof IssueNavigatorUI,
		ComponentProps<typeof IssueNavigatorUI>
	>['onChangeIssue'];
	onChangeFilter?: (filterId?: string) => void;
	onRefinement?: () => void;
	/**
	 * Callback function called when internal changes are made to NINs state which generates new JQL and groupBy.
	 * This allows us to update both values in the URL at the same time, allowing the data to be passed back down the tree.
	 * This is introduced strictly for jira_list_nin_merge changeboarding, and can be removed on cleanup.
	 */
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
	/**
	 * Event emitted when the page has loaded and the key experience is interactive. There are several different user
	 * landing states this encompasses:
	 * - Detail view is rendered with issues loaded
	 * - List view is rendered with issues loaded
	 * - Full page issue app pagination controls are rendered (i.e. list view with an issue selected)
	 */
	onPageDataLoad?: (selectedView: IssueNavigatorViewId) => void;
	onChangeColumnConfiguration?: () => void;
	onChangeJql?: JSX.LibraryManagedAttributes<
		typeof IssueNavigatorUI,
		ComponentProps<typeof IssueNavigatorUI>
	>['onChangeJql'];
	onSetView: (view: IssueNavigatorViewId, issueKey: IssueKey) => void;
	jqlBuilderProps?: OverridableJqlBuilderProps;
	/**
	 * This overrides a subset of issue table props when rendering the issue table in NIN.
	 */
	issueTableProps?: OverridableIssueTableProps;
	/**
	 * This prop is used to determine if the issue hierarchy feature is enabled in the experience.
	 */
	isIssueHierarchySupportEnabled?: boolean;
	/**
	 * This prop is used to determine if the re-ranking feature is enabled
	 */
	isIssueRankingSupportEnabled?: boolean;
	/**
	 * This prop is used to determine if the merged column feature is enabled
	 */
	isMergedColumnSupportEnabled?: boolean;
	/**
	 * This prop is used to determine if the frozen column feature is enabled
	 */
	isFrozenColumnSupportEnabled?: boolean;
	/**
	 * This prop is used to determine if the ifc feature is enabled
	 */
	isInlineFieldConfigEnabled?: boolean;
	/**
	 * This prop is used to determine if the in between column picker is enabled
	 */
	isInBetweenColumnPickerEnabled?: boolean;
	/**
	 * This prop is used to determine if grid navigation is enabled
	 */
	isGridNavigationEnabled?: boolean;
	/**
	 * This prop is used to determine if the post office placement is enabled
	 * Clean up when cleaning jira_list_nin_merge FG
	 */
	isPostOfficePlacementEnabled?: boolean;
	/**
	 * This prop is used to determine if the filter refinement feature is enabled
	 */
	isFilterRefinementEnabled?: boolean;
	/**
	 * Preloaded query reference for the filter refinement component.
	 */
	filterRefinementQueryReference?: PreloadedQuery<JQLBuilderWithAIFilterRefinementQuery> | null;
};

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files, @atlassian/relay/must-use-inline
export const FilterQuery = graphql`
	query main_FilterQuery($filterAri: ID!, $includeFilter: Boolean!) {
		...main_issueNavigator_IssueNavigator_filterQuery
	}
`;

type FilterAndJqlProviderProps = {
	children: ReactNode;
	jql: string | undefined;
	refetch: RefetchFn<FilterRefetchQuery>;
	filterId: string | undefined;
	isFilterEditable: boolean | undefined;
	filterJql?: string;
};

// We memo as an optimisation to reduce forced re-renders when the immediate parent renders. In this case it's
// AppContextualAnalytics which will re-render when interacting with the JQL builder, e.g. opening picker.
const FilterAndJqlProvider = memo(
	({
		children,
		jql,
		refetch,
		filterId,
		isFilterEditable,
		filterJql,
	}: FilterAndJqlProviderProps) => (
		<FilterQueryProvider query={FilterQuery} refetch={refetch} filterId={filterId}>
			<ActiveJqlProvider
				filterId={filterId}
				filterJql={filterJql}
				jql={jql}
				isFilterEditable={isFilterEditable}
			>
				{children}
			</ActiveJqlProvider>
		</FilterQueryProvider>
	),
);

const MaybeFilterAndJqlProvider = componentWithFG(
	'jira_nin_last_used_jql_tech_changes',
	({ children }: FilterAndJqlProviderProps) => children,
	FilterAndJqlProvider,
);
const MaybeIssueSearchInputProvider = componentWithFG(
	'jira_nin_last_used_jql_tech_changes',
	IssueSearchInputProvider,
	({ children }: ComponentPropsWithoutRef<typeof IssueSearchInputProvider>) => children,
);

const getFilter = (searchParam: SearchInputType): FilterJql | FilterId | undefined => {
	if (searchParam?.type === SearchInputTypes.JQL) {
		return undefined;
	}
	return searchParam.type === SearchInputTypes.FILTER_AND_JQL ? searchParam.filter : searchParam;
};

const IssueNavigator = ({
	issueKey,
	queryReference,
	filterQueryReference,
	CustomHeader,
	ActionMenu,
	HeaderSkeletonImage,
	onChangeIssue,
	onChangeFilter,
	onRefinement,
	onChangeJqlAndGroupBy,
	onPageDataLoad: onPageDataLoadProp,
	onChangeColumnConfiguration,
	onChangeJql,
	onSetView,
	defaultJql,
	searchInput,
	onFilterKeyToJql,
	jqlBuilderProps,
	issueTableProps,
	isIssueHierarchySupportEnabled,
	isIssueRankingSupportEnabled,
	isMergedColumnSupportEnabled,
	isFrozenColumnSupportEnabled,
	isInBetweenColumnPickerEnabled,
	isInlineFieldConfigEnabled,
	isGridNavigationEnabled,
	isPostOfficePlacementEnabled,
	isFilterRefinementEnabled = false,
	filterRefinementQueryReference,
}: Props) => {
	markOnce(marks.ISSUE_NAVIGATOR_START);
	useLayoutEffect(() => {
		markOnce(marks.ISSUE_NAVIGATOR_END);
	}, []);
	const [isInitialized, setIsInitialized] = useState(false);
	const onPageDataLoad: NonNullable<Props['onPageDataLoad']> = useCallback(
		(...props) => {
			if (!isInitialized) {
				setIsInitialized(true);
			}
			return onPageDataLoadProp?.(...props);
		},
		[isInitialized, onPageDataLoadProp],
	);

	const filterInput = searchInput ? getFilter(searchInput) : undefined;

	const issueQuery = usePreloadedQuery<IssueNavigatorQueryType>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query main_IssueNavigatorQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$namespace: String
				$viewId: String
				$options: JiraIssueSearchOptions
				$fieldSetsInput: JiraIssueSearchFieldSetsInput
				$filterId: String
				$amountOfColumns: Int!
				$atlassianIntelligenceProductFeature: JiraAtlassianIntelligenceFeatureEnum!
				$projectKey: String!
				$shouldQueryProject: Boolean!
				$shouldQueryConditionalFormatting: Boolean!
				$shouldQueryConditionalFormattingFromSavedView: Boolean!
				$isPaginating: Boolean = false
				$staticViewInput: JiraIssueSearchStaticViewInput
				$viewConfigInput: JiraIssueSearchViewConfigInput
				$scope: JiraIssueSearchScope
				$groupBy: String
				$shouldQueryHasChildren: Boolean = false
				$fieldSetsScope: JiraIssueSearchScope
				$viewQueryInput: JiraIssueSearchViewQueryInput
				$settings: JiraIssueSearchSettings
				$saveJQLToUserHistory: Boolean = false
			) {
				...main_issueNavigator_IssueNavigator_issueQuery
					@arguments(
						projectKeys: [$projectKey]
						projectKey: $projectKey
						isConditionalFormattingEnabled: $shouldQueryConditionalFormatting # replace shouldQueryConditionalFormatting with shouldQueryConditionalFormattingFromSavedView when cleaning up saved_list_view_technical_changes
						isSavedViewConditionalFormattingEnabled: $shouldQueryConditionalFormattingFromSavedView
					)
				jira {
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						...main_issueNavigator_IssueNavigator_userPreferences
					}
					...main_issueNavigator_IssueNavigator_jira
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @include(if: $shouldQueryProject) {
						...main_issueNavigator_IssueNavigatorUI_project
							@arguments(
								isConditionalFormattingEnabled: $shouldQueryConditionalFormatting
								isSavedViewConditionalFormattingEnabled: $shouldQueryConditionalFormattingFromSavedView
							)
						...issueSearch_issueNavigatorProject
						...container_issueNavigatorActionConditionalFormatting_ConditionalFormattingInner
							@arguments(first: 20)
							@include(if: $shouldQueryConditionalFormatting)
							@skip(if: $shouldQueryConditionalFormattingFromSavedView)
						...ConditionalFormattingSidebarContainer_project
							@include(if: $shouldQueryConditionalFormattingFromSavedView)
					}
				}
			}
		`,
		queryReference,
	);

	const refetchQuery = useFragment<IssueSearchConditionFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_issueQuery on Query
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!" }
			) {
				...main_issueNavigator_IssueNavigator_refetchQuery
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
					)
			}
		`,
		issueQuery,
	);

	const [issueNavigatorRefetchDataNew, issueNavigatorRefetchNew] = useRefetchableFragment<
		IssueNavigatorIssueSearchRefetchQuery,
		IssueNavigatorRefetchFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_refetchQuery on Query
			@refetchable(queryName: "IssueNavigatorIssueSearchRefetchQuery")
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isSavedViewConditionalFormattingEnabled: { type: "Boolean!" }
				isLastUsedJqlEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-nin-last-used-jql.relayprovider"
				}
			) {
				jira {
					jiraIssueSearchView(
						cloudId: $cloudId
						namespace: $namespace
						viewId: $viewId
						issueSearchInput: $issueSearchInput
						viewConfigInput: $staticViewInput
						filterId: $filterId
						viewQueryInput: $viewQueryInput
						scope: $scope
						settings: $settings
					) @optIn(to: "JiraIssueSearchView") {
						__typename
						...IssueSearchInputProvider_jiraView
						...issueSearch_issueNavigatorGrouping
						...main_issueNavigator_IssueNavigatorUI_jiraView
							@arguments(
								isSavedViewConditionalFormattingEnabled: $isSavedViewConditionalFormattingEnabled
							)
						...selectedView_issueNavigator_SelectedViewContainer
						... on JiraSpreadsheetView {
							...ConditionalFormattingSidebarContainer_view
								@arguments(first: 20)
								@include(if: $isSavedViewConditionalFormattingEnabled)
						}
						... on JiraDetailedView {
							...main_issueNavigator_IssueNavigator_jiraDetailedViewData
								@arguments(
									projectKeys: $projectKeys
									projectKey: $projectKey
									isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								)
								@include(if: $isLastUsedJqlEnabled)
							...main_issueNavigator_IssueNavigator_detailedViewData
								@arguments(
									projectKeys: $projectKeys
									projectKey: $projectKey
									isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								)
								@skip(if: $isLastUsedJqlEnabled)
						}
						... on JiraGroupedListView {
							...main_issueNavigator_IssueNavigator_jiraGroupedListViewData
								@arguments(projectKeys: $projectKeys, projectKey: $projectKey)
								@include(if: $isLastUsedJqlEnabled)
							...main_issueNavigator_IssueNavigator_groupedListViewData
								@arguments(projectKeys: $projectKeys, projectKey: $projectKey)
								@skip(if: $isLastUsedJqlEnabled)
						}
						... on JiraListView {
							...main_issueNavigator_IssueNavigator_jiraListViewData
								@arguments(
									projectKeys: $projectKeys
									projectKey: $projectKey
									shouldQueryHasChildren: $shouldQueryHasChildren
									isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								)
								@include(if: $isLastUsedJqlEnabled)
							...main_issueNavigator_IssueNavigator_listViewData
								@arguments(
									projectKeys: $projectKeys
									projectKey: $projectKey
									shouldQueryHasChildren: $shouldQueryHasChildren
									isConditionalFormattingEnabled: $isConditionalFormattingEnabled
								)
								@skip(if: $isLastUsedJqlEnabled)
						}
					}
				}
			}
		`,
		refetchQuery,
	);

	const jiraViewData = issueNavigatorRefetchDataNew?.jira?.jiraIssueSearchView ?? null;

	const detailedViewPaginationFragmentOld = usePaginationFragment<
		IssueNavigatorDetailedViewPaginationQuery,
		DetailedViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_detailedViewData on JiraDetailedView
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
			)
			@refetchable(queryName: "IssueNavigatorDetailedViewPaginationQuery") {
				issues(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					options: $options
					fieldSetsInput: $fieldSetsInput
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				)
					@connection(
						key: "IssueNavigatorDetailedViewPagination__issues"
						filters: ["issueSearchInput"]
					) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...selectedIssue_issueNavigator
					...main_issueNavigator_IssueNavigatorUI_issueResults
						@arguments(
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: false
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraDetailedView' && !fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);
	const jiraDetailedViewPaginationFragment = usePaginationFragment<
		IssueNavigatorJiraDetailedViewPaginationQuery,
		JiraDetailedViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_jiraDetailedViewData on JiraDetailedView
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
			)
			@refetchable(queryName: "IssueNavigatorJiraDetailedViewPaginationQuery") {
				issues(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					options: $options
					fieldSetsInput: $fieldSetsInput
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				) @connection(key: "IssueNavigatorJiraDetailedViewPagination__issues", filters: ["scope"]) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...selectedIssue_issueNavigator
					...main_issueNavigator_IssueNavigatorUI_issueResults
						@arguments(
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: false
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraDetailedView' && fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);

	const groupedListViewPaginationFragmentOld = usePaginationFragment<
		IssueNavigatorGroupedListViewPaginationQuery,
		GroupedListViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_groupedListViewData on JiraGroupedListView
			@argumentDefinitions(projectKeys: { type: "[String!]" }, projectKey: { type: "String" })
			@refetchable(queryName: "IssueNavigatorGroupedListViewPaginationQuery") {
				groups(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					groupBy: $groupBy
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				)
					@connection(
						key: "IssueNavigatorGroupedListViewPagination__groups"
						filters: ["issueSearchInput"]
					) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...main_issueNavigator_IssueNavigatorUI_groupResults
						@arguments(projectKeys: $projectKeys, projectKey: $projectKey)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraGroupedListView' && !fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);

	const jiraGroupedListViewPaginationFragment = usePaginationFragment<
		IssueNavigatorJiraGroupedListViewPaginationQuery,
		JiraGroupedListViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_jiraGroupedListViewData on JiraGroupedListView
			@argumentDefinitions(projectKeys: { type: "[String!]" }, projectKey: { type: "String" })
			@refetchable(queryName: "IssueNavigatorJiraGroupedListViewPaginationQuery") {
				groups(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					groupBy: $groupBy
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				)
					@connection(
						key: "IssueNavigatorJiraGroupedListViewPagination__groups"
						filters: ["scope"]
					) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...main_issueNavigator_IssueNavigatorUI_groupResults
						@arguments(projectKeys: $projectKeys, projectKey: $projectKey)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraGroupedListView' && fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);

	const listViewPaginationFragmentOld = usePaginationFragment<
		IssueNavigatorListViewPaginationQuery,
		ListViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_listViewData on JiraListView
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
			)
			@refetchable(queryName: "IssueNavigatorListViewPaginationQuery") {
				issues(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					options: $options
					viewConfigInput: $viewConfigInput
					fieldSetsInput: $fieldSetsInput
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				)
					@connection(
						key: "IssueNavigatorListViewPagination__issues"
						filters: ["issueSearchInput"]
					) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...selectedIssue_issueNavigator
					...main_issueNavigator_IssueNavigatorUI_issueResults
						@arguments(
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: $shouldQueryHasChildren
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraListView' && !fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);

	const jiraListViewPaginationFragment = usePaginationFragment<
		IssueNavigatorJiraListViewPaginationQuery,
		JiraListViewDataFragment
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_jiraListViewData on JiraListView
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
			)
			@refetchable(queryName: "IssueNavigatorJiraListViewPaginationQuery") {
				issues(
					issueSearchInput: $issueSearchInput
					first: $first
					last: $last
					before: $before
					after: $after
					options: $options
					viewConfigInput: $viewConfigInput
					fieldSetsInput: $fieldSetsInput
					scope: $scope
					settings: $settings
					saveJQLToUserHistory: $saveJQLToUserHistory
				) @connection(key: "IssueNavigatorJiraListViewPagination__issues", filters: ["scope"]) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						cursor
					}
					pageInfo {
						endCursor
						startCursor
					}
					...selectedIssue_issueNavigator
					...main_issueNavigator_IssueNavigatorUI_issueResults
						@arguments(
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: $shouldQueryHasChildren
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						)
				}
			}
		`,
		jiraViewData?.__typename === 'JiraListView' && fg('jira_nin_last_used_jql')
			? jiraViewData
			: null,
	);

	const detailedViewPaginationFragment = fg('jira_nin_last_used_jql')
		? jiraDetailedViewPaginationFragment
		: detailedViewPaginationFragmentOld;
	const groupedListViewPaginationFragment = fg('jira_nin_last_used_jql')
		? jiraGroupedListViewPaginationFragment
		: groupedListViewPaginationFragmentOld;
	const listViewPaginationFragment = fg('jira_nin_last_used_jql')
		? jiraListViewPaginationFragment
		: listViewPaginationFragmentOld;

	const userPreferencesData = useFragment<UserPreferencesFragmentKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_userPreferences on JiraUserPreferences {
				...main_issueNavigator_IssueNavigatorUI_userPreferences
			}
		`,
		issueQuery.jira?.userPreferences ?? null,
	);

	const jqlBuilderWithAiData = useFragment<JqlBuilderWithAiKey>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_jira on JiraQuery {
				...main_issueNavigator_IssueNavigatorUI_jqlBuilderWithAiKey
			}
		`,
		issueQuery.jira,
	);

	let activePaginationFragment:
		| typeof detailedViewPaginationFragment
		| typeof groupedListViewPaginationFragment
		| typeof listViewPaginationFragment = listViewPaginationFragment;
	let groupResultsData = null;
	let issueResultsData = listViewPaginationFragment?.data?.issues ?? null;

	if (detailedViewPaginationFragment.data) {
		activePaginationFragment = detailedViewPaginationFragment;
		issueResultsData = detailedViewPaginationFragment?.data?.issues ?? null;
	} else if (groupedListViewPaginationFragment.data) {
		activePaginationFragment = groupedListViewPaginationFragment;
		groupResultsData = groupedListViewPaginationFragment?.data?.groups ?? null;
	}

	const filterQuery = usePreloadedQuery<FilterQueryType>(FilterQuery, filterQueryReference);
	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	const [filterData, refetchFilter] = useRefetchableFragment<FilterRefetchQuery, FilterFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueNavigator_filterQuery on Query
			@refetchable(queryName: "FilterRefetchQuery") {
				jira {
					filter(id: $filterAri) @include(if: $includeFilter) {
						... on JiraFilter {
							jql
							... on JiraCustomFilter {
								isEditable
							}
							...IssueSearchInputProvider_jiraFilter
							...main_issueNavigator_Header_filter
							...topBar_issueNavigator_filter
							...jqlBuilder_issueNavigator_JQLBuilderWrapper_filter
							...main_issueNavigator_ListView_filter
						}
					}
				}
			}
		`,
		filterQuery,
	);
	/* eslint-enable @atlassian/relay/must-colocate-fragment-spreads */

	/**
	 * initialQueryAsJql remembers the initial query on mount
	 */
	const [initialQueryAsJql] = useState(() => {
		if (
			!fg('jracloud_85520_hydration_ref_only_on_page_load') ||
			fg('jira_nin_last_used_jql_tech_changes')
		) {
			return undefined;
		}

		// Use base input for plain JQL input OR
		// If both filter and JQL are present, JQL takes precedence
		if (
			searchInput.type === SearchInputTypes.JQL ||
			searchInput.type === SearchInputTypes.FILTER_AND_JQL
		) {
			return searchInput.jql;
		}

		// Use filter JQL for manually-mapped filters like we use in Project Scope
		if (filterInput?.type === FilterTypes.JQL) {
			return filterInput.value;
		}

		// Otherwise, use the saved filter JQL as returned by the backend
		return filterData?.jira?.filter?.jql;
	});

	let setFlatListIssues = noop;
	if (fg('jsc_m2_hierarchy_fe_changes')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ setFlatListIssues } = useFlatListIssuesActions());
	}

	const handleIssueFlatListUpdated = fg('jsc_m2_hierarchy_fe_changes')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useCallback(
				(newFlatKeyList: string[]) => {
					setFlatListIssues(newFlatKeyList);
				},
				[setFlatListIssues],
			)
		: noop;

	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEnabled;

	return (
		<AppContextualAnalytics>
			<ConditionalFormatting
				isEnabled={isConditionalFormattingEnabled}
				project={issueQuery.jira?.jiraProjectByKey ?? null}
				view={
					isConditionalFormattingEnabled && fg('saved_list_view_technical_changes')
						? jiraViewData
						: null
				}
			>
				<SidebarContainer scope={NIN_SIDEBAR_SCOPE} sidebarId={ISSUE_NAVIGATOR_SIDEBAR_ID}>
					<MaybeIssueSearchInputProvider
						defaultJql={defaultJql}
						filterQuery={FilterQuery}
						jiraFilter={filterData?.jira?.filter ?? null}
						jiraView={jiraViewData}
						onFilterKeyToJql={onFilterKeyToJql}
						refetch={refetchFilter}
					>
						<MaybeFilterAndJqlProvider
							refetch={refetchFilter}
							filterId={filterInput?.type === FilterTypes.ID ? filterInput?.value : undefined}
							filterJql={
								filterInput?.type === FilterTypes.JQL
									? filterInput?.value
									: filterData?.jira?.filter?.jql
							}
							isFilterEditable={
								filterData.jira?.filter?.isEditable !== null
									? filterData.jira?.filter?.isEditable
									: undefined
							}
							jql={
								searchInput?.type === SearchInputTypes.FILTER_AND_JQL ||
								searchInput?.type === SearchInputTypes.JQL
									? searchInput?.jql
									: undefined
							}
						>
							<IssueSearch
								onPageDataLoad={onPageDataLoad}
								jiraView={jiraViewData}
								refetch={issueNavigatorRefetchNew}
								isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
								isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
								project={issueQuery.jira?.jiraProjectByKey ?? null}
								{...(fg('jsc_m2_hierarchy_fe_changes') && {
									issueTableProps,
								})}
							>
								<InfiniteScrollProvider
									onLoadNext={activePaginationFragment.loadNext}
									onLoadPrevious={activePaginationFragment.loadPrevious}
									isLoadingNext={activePaginationFragment.isLoadingNext}
									isLoadingPrevious={activePaginationFragment.isLoadingPrevious}
									hasNext={activePaginationFragment.hasNext}
									hasPrevious={activePaginationFragment.hasPrevious}
								>
									<SelectedViewContainer onSetView={onSetView} jiraView={jiraViewData}>
										<SelectedIssue
											selectedIssueKey={issueKey}
											onChange={onChangeIssue}
											issues={issueResultsData}
										>
											<IssueNavigatorUI
												CustomHeader={CustomHeader}
												ActionMenu={ActionMenu}
												HeaderSkeletonImage={HeaderSkeletonImage}
												filter={filterData?.jira?.filter ?? null}
												onChangeIssue={onChangeIssue}
												onChangeFilter={onChangeFilter}
												onChangeJqlAndGroupBy={onChangeJqlAndGroupBy}
												onPageDataLoad={onPageDataLoad}
												onChangeColumnConfiguration={onChangeColumnConfiguration}
												onChangeJql={onChangeJql}
												onRefinement={onRefinement}
												defaultJql={defaultJql}
												issueResults={issueResultsData}
												groupResults={groupResultsData}
												project={issueQuery.jira?.jiraProjectByKey ?? null}
												userPreferences={userPreferencesData ?? null}
												jqlBuilderProps={jqlBuilderProps}
												jqlBuilderWithAiKey={jqlBuilderWithAiData ?? null}
												issueTableProps={issueTableProps}
												isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
												jiraView={jiraViewData}
												onIssueFlatListUpdated={handleIssueFlatListUpdated}
												initialQueryAsJql={initialQueryAsJql}
												isInitialized={isInitialized}
												isIssueRankingSupportEnabled={isIssueRankingSupportEnabled}
												isMergedColumnSupportEnabled={isMergedColumnSupportEnabled}
												isFrozenColumnSupportEnabled={isFrozenColumnSupportEnabled}
												isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
												isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
												isGridNavigationEnabled={isGridNavigationEnabled}
												isPostOfficePlacementEnabled={isPostOfficePlacementEnabled}
												isFilterRefinementEnabled={isFilterRefinementEnabled}
												filterRefinementQueryReference={filterRefinementQueryReference}
											/>
										</SelectedIssue>
									</SelectedViewContainer>
								</InfiniteScrollProvider>
							</IssueSearch>
						</MaybeFilterAndJqlProvider>
					</MaybeIssueSearchInputProvider>
				</SidebarContainer>
			</ConditionalFormatting>
			{fg('currency-field-d3-changeboarding') && <CurrencyFieldChangeboarding />}
		</AppContextualAnalytics>
	);
};

export const ConditionalFormatting = ({
	isEnabled,
	project,
	view,
	children,
}: React.PropsWithChildren<{
	isEnabled?: boolean;
	project:
		| ConditionalFormattingSidebarContainer_project$key
		| container_issueNavigatorActionConditionalFormatting_ConditionalFormattingInner$key
		| null;
	view?: ConditionalFormattingSidebarContainer_view$key | null;
}>) => {
	if (fg('saved_list_view_technical_changes')) {
		return (
			<ConditionalFormattingSidebarContainer
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				project={project as ConditionalFormattingSidebarContainer_project$key | null}
				isEnabled={isEnabled}
				view={view ?? null}
			>
				{children}
			</ConditionalFormattingSidebarContainer>
		);
	}

	return (
		<ConditionalFormattingOld
			isEnabled={isEnabled}
			project={
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				project as container_issueNavigatorActionConditionalFormatting_ConditionalFormattingInner$key | null
			}
		>
			{children}
		</ConditionalFormattingOld>
	);
};

const WithFlatListIssuesContainer = (Component: React.ComponentType<Props>) => {
	const IssueNavigatorWithFlatListIssuesContainer = (props: Props) => {
		return (
			<FlatListIssuesContainer>
				<Component {...props} />
			</FlatListIssuesContainer>
		);
	};
	return IssueNavigatorWithFlatListIssuesContainer;
};
const ConditionalIssueNavigator = componentWithCondition(
	() => fg('jsc_m2_hierarchy_fe_changes'),
	WithFlatListIssuesContainer(IssueNavigator),
	IssueNavigator,
);

export default memo<Props>(ConditionalIssueNavigator);
